import {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  FindInPage as FindInPageIcon,
} from '@material-ui/icons';

import { Modal } from 'components';
import { upload, getSignedUrl } from 'utils/aws/s3';
import { parseIfValid } from 'utils/json';
import { DOCUMENT_VIEWER } from '../Document2Control/constants';

const DOCUMENT_EXTENSIONS = ['doc', 'docx'];
const IMAGE_EXTENSIONS = ['gif', 'jpg', 'jpeg', 'png', 'webp'];

const getFileExtension = (url = '') => String(url).split('?')[0].split('.').pop()?.toLowerCase();
const getDocumentViewerUrl = (url = '') => `${DOCUMENT_VIEWER}?${new URLSearchParams({
  embedded: 'true',
  url,
}).toString()}`;

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
  },
  labelBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontWeight: 700,
    fontSize: 13,
    color: '#0F172A',
  },
  status: {
    fontSize: 12,
    color: '#94A3B8',
  },
  fileName: {
    fontSize: 12,
    color: '#0F172A',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  previewButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    padding: '6px 14px',
  },
  attachButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    padding: '6px 16px',
  },
  deleteButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    padding: '6px 14px',
  },
  retryButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    padding: '6px 14px',
  },
  verificationError: {
    fontSize: 12,
    color: '#DC2626',
    fontWeight: 600,
    marginTop: 4,
  },
  previewWrapper: {
    position: 'relative',
    minHeight: 200,
  },
  previewLoader: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    zIndex: 1,
  },
}));

function DocumentInlineControl(props) {
  const classes = useStyles();
  const {
    disabled,
    error,
    events = {},
    helperText,
    label,
    description,
    name,
    onChange,
    placeholder,
    settings = {},
    value,
  } = props;
  const fileId = `frm_inline_file_${name}`;
  const [state, setState] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [modal, setModal] = useState();
  const previewObjectUrlRef = useRef();

  const getFileType = (url = '') => {
    const ext = getFileExtension(url);
    if (IMAGE_EXTENSIONS.includes(ext)) {
      return 'image';
    }
    if (ext === 'pdf') {
      return 'pdf';
    }
    if (DOCUMENT_EXTENSIONS.includes(ext)) {
      return 'document';
    }
    return 'other';
  };

  const clearPreviewObjectUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = undefined;
    }
  };

  const loadPdfPreview = async (signedUrl) => {
    try {
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error('No se pudo cargar el PDF.');
      }

      const blob = await response.blob();
      const pdfBlob = blob.type === 'application/pdf'
        ? blob
        : new Blob([blob], {
          type: 'application/pdf',
        });
      const previewUrl = URL.createObjectURL(pdfBlob);

      clearPreviewObjectUrl();
      previewObjectUrlRef.current = previewUrl;
      setModal((current) => current ? ({
        ...current,
        data: {
          ...current.data,
          previewUrl,
        },
      }) : current);
    } catch (error) {
      setModal((current) => current ? ({
        ...current,
        data: {
          ...current.data,
          previewError: true,
        },
      }) : current);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSelect = () => {
    const input = document.getElementById(fileId);
    if (input) {
      input.click();
    }
  };

  const handleChangeFile = ({ target }) => {
    const [file] = target.files;
    if (!file) return;
    setPendingFiles([file]);
  };

  const handleDelete = () => {
    setState([]);
  };

  const handleRetry = () => {
    handleDelete();
    handleSelect();
  };

  const handlePreview = () => {
    const currentFile = state.find((item) => item?.active);
    if (!currentFile?.signedUrl) return;
    const fileType = getFileType(currentFile?.url || currentFile?.signedUrl);
    clearPreviewObjectUrl();
    setIsPreviewLoading(fileType === 'document' || fileType === 'image' || fileType === 'pdf');
    setModal({
      open: true,
      view: 'preview',
      title: label || 'Vista previa',
      data: {
        ...currentFile,
        previewError: false,
        previewUrl: fileType === 'pdf' ? '' : currentFile.signedUrl,
        type: fileType,
      },
      maxWidth: fileType === 'image' ? 'sm' : 'md',
    });

    if (fileType === 'pdf') {
      loadPdfPreview(currentFile.signedUrl);
    }
  };

  const loadData = async () => {
    let initialValue = [];
    if (typeof value === 'string') {
      initialValue = parseIfValid(value, false) || [];
    } else if (Array.isArray(value)) {
      initialValue = value;
    }

    for await (const item of initialValue) {
      if (item?.active && item?.url) {
        item.signedUrl = await getSignedUrl(item.url, 1000);
      }
    }

    setState(initialValue);
  };

  const uploadFile = async () => {
    const [file] = pendingFiles;
    if (!file) return;

    const ext = ((file?.name ?? '').split('.').pop() || '').toLowerCase();
    const route = settings?.route ?? 'tmp/';
    const metadata = {};

    setIsLoading(true);
    setUploadError(false);

    for (let attempt = 1; attempt <= 5; attempt++) {
      try {
        const s3File = await upload(
          `${route}${new Date().getTime()}.${String(Math.random()).substring(
            2,
            7,
          )}${ext ? `.${ext}` : ''}`,
          file,
          metadata,
        );

        const signedUrl = await getSignedUrl(s3File?.Location, 1000);

        setState([
          {
            active: true,
            metadata,
            name,
            uploaded: new Date().getTime(),
            url: s3File?.Location,
            signedUrl,
            filename: file?.name,
          },
        ]);

        setPendingFiles([]);
        setIsLoading(false);
        return;
      } catch (error) {
        if (attempt === 5) {
          setUploadError(true);
          setPendingFiles([]);
          setIsLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    loadData();

    return clearPreviewObjectUrl;
  }, []);

  useEffect(() => {
    if (pendingFiles.length > 0 && !uploadError) {
      uploadFile();
    }
  }, [pendingFiles]);

  useEffect(() => {
    const target = {
      name,
      value: state.map((item) => ({
        active: item.active,
        metadata: item.metadata ?? {},
        name: item.name,
        uploaded: item.uploaded,
        url: item.url,
      })),
    };
    onChange({
      target,
    });
    if (events?.onChange) {
      events.onChange(target);
    }
  }, [state]);

  const currentFile = state.find((item) => item?.active);
  const statusText = currentFile
    ? (currentFile.filename || String(currentFile.url ?? '').split('/').pop())
    : (isLoading ? 'Cargando...' : (description ? 'Pendiente' : (placeholder || 'Pendiente')));
  const verificationFailed = Boolean(settings?.verificationFailed || currentFile?.metadata?.verificationFailed);
  const verificationMessage = settings?.verificationMessage || currentFile?.metadata?.verificationMessage;

  const normalizeNewlines = (s) => {
    if (typeof s !== 'string') return s;
    let next = s;
    while (next.includes('\\n')) {
      next = next.replace(/\\n/g, '\n');
    }
    return next;
  };
  const renderedDescription = normalizeNewlines(description);

  return (
    <>
      <Modal
        actions={[]}
        events={{
          onClose: () => {
            setModal();
            setIsPreviewLoading(false);
            clearPreviewObjectUrl();
          },
        }}
        maxWidth={modal?.maxWidth}
        open={modal?.open}
        title={modal?.title}
      >
        {(modal?.view === 'preview') && (
          <Box className={classes.previewWrapper}>
            {isPreviewLoading && (
              <Box className={classes.previewLoader}>
                <CircularProgress size={28} color="secondary" />
              </Box>
            )}
            {modal?.data?.type === 'document' && (
              <iframe
                frameBorder="0"
                height="500px"
                onLoad={() => setIsPreviewLoading(false)}
                src={getDocumentViewerUrl(modal?.data?.signedUrl)}
                title="Vista previa"
                width="100%"
              />
            )}
            {modal?.data?.type === 'pdf' && modal?.data?.previewUrl && (
              <iframe
                frameBorder="0"
                height="500px"
                src={modal?.data?.previewUrl}
                title="Vista previa"
                width="100%"
              />
            )}
            {modal?.data?.type === 'pdf' && modal?.data?.previewError && (
              <Box textAlign="center">
                <FindInPageIcon fontSize="large" />
                <Typography variant="body2" style={{
                  marginTop: 8,
                }}>
                  Vista previa no disponible
                </Typography>
              </Box>
            )}
            {modal?.data?.type === 'image' && (
              <img
                alt=""
                onLoad={() => setIsPreviewLoading(false)}
                onError={() => setIsPreviewLoading(false)}
                src={modal?.data?.signedUrl}
                style={{
                  width: '100%',
                  display: 'block',
                }}
              />
            )}
            {modal?.data?.type === 'other' && (
              <Box textAlign="center">
                <FindInPageIcon fontSize="large" />
                <Typography variant="body2" style={{
                  marginTop: 8,
                }}>
                  Vista previa no disponible
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Modal>
      <input
        id={fileId}
        onChange={handleChangeFile}
        style={{
          display: 'none',
        }}
        type="file"
      />
      <Box className={classes.row}>
        <Box className={classes.labelBlock}>
          <Typography className={classes.label}>
            {label}
          </Typography>
          {renderedDescription && (
            <Typography
              className={classes.status}
              component="div"
              style={{
                fontStyle: 'normal',
                whiteSpace: 'pre-line',
                marginTop: 4,
              }}
            >
              {renderedDescription}
            </Typography>
          )}
          <Typography className={currentFile ? classes.fileName : classes.status}>
            {statusText}
          </Typography>
          {verificationFailed && (
            <Typography className={classes.verificationError}>
              {verificationMessage || 'La validación del documento falló. Reintenta.'}
            </Typography>
          )}
          {uploadError && (
            <Typography color="error" variant="caption">
              Fallo la carga. Intenta de nuevo.
            </Typography>
          )}
        </Box>
        <Box className={classes.actions}>
          {currentFile ? (
            <>
              <Button
                className={classes.previewButton}
                disabled={disabled || isLoading}
                onClick={handlePreview}
                startIcon={<FindInPageIcon />}
                variant="outlined"
                color="primary"
              >
                Ver
              </Button>
              {verificationFailed ? (
                <Button
                  className={classes.retryButton}
                  disabled={disabled || isLoading}
                  onClick={handleRetry}
                  variant="outlined"
                  color="secondary"
                >
                  Reintentar
                </Button>
              ) : (
                <Button
                  className={classes.deleteButton}
                  disabled={disabled || isLoading}
                  onClick={handleDelete}
                  startIcon={<DeleteOutlineIcon />}
                  variant="outlined"
                  color="secondary"
                >
                  Eliminar
                </Button>
              )}
            </>
          ) : (
            <Button
              className={classes.attachButton}
              color="secondary"
              disabled={disabled || isLoading}
              onClick={handleSelect}
              startIcon={isLoading
                ? <CircularProgress size={18} color="inherit" />
                : <CloudUploadOutlinedIcon />}
              variant="contained"
            >
              {isLoading ? 'Adjuntando...' : 'Adjuntar'}
            </Button>
          )}
        </Box>
      </Box>
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </>
  );
}

DocumentInlineControl.defaultProps = {
  disabled: false,
  onChange: () => { },
  settings: {},
};

DocumentInlineControl.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.string,
  events: PropTypes.shape({
    onChange: PropTypes.func,
  }),
  helperText: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  settings: PropTypes.shape({
    verificationFailed: PropTypes.bool,
    verificationMessage: PropTypes.string,
    route: PropTypes.string,
  }),
  value: PropTypes.any,
};

export default DocumentInlineControl;
